/**
 * OutpostModule — each side's FIELD BASE in a battle (war campaign 2026-09-16).
 *
 * Reads `battle_outpost` rows and, for every outpost within range, builds a
 * small forward base out of the GENERATED outpost set (the Hugging Face
 * outpost wave, 2026-09-17 — lib/outpost_props: wall panels, gate pillars, the
 * aid station, the repair bay, a bunkhouse, a comms mast, a turret that aims),
 * falling back per part to the older cooked props and boxes while the set
 * streams: a fence square (`prop:hinter_fence` segments) with a gate
 * toward the field, barrels and a brazier, a comms relay, the medics' AID
 * STATION (`prop:market_stall`) and the mechanics' BAY (a stall + barrels),
 * a banner pole in the side's colour, and two PROCEDURAL TURRETS at the
 * field-side corners that track the last `turret` battle_event and flash on
 * it. A missing prop key keeps that part procedural (the hinterland's
 * self-heal law). Structures are the cooked GLBs, seated by their bbox
 * bottom-centre (the PIVOT law). Nothing here decides anything.
 *
 * Levers: `window.__outposts()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../../state.js';
import { bus } from '../../bus.js';
import { makeSystem, Phase } from '../../engine-compat.js';
import { prefabAssetId } from '../../lib/player_models.js';
import { markShared } from '../../lib/defer_dispose.js';
import { disposeLater } from '../../lib/gpu_dispose.js';
import { fireAnchor } from '../../lib/vfx/fire_system.js';
import { glowTexture, spriteOf } from './fx_textures.js';
import { prefetchOutpostProps, outpostProp, outpostPropStats, outpostPropSize } from './outpost_props_loader.js';
import type { AssetModule } from '../AssetModule.js';
import type { EnvironmentModule } from '../EnvironmentModule.js';
import { BATTLE } from '@shared/living_events.js';

export interface OutpostRow { outpostId: bigint; battleId: bigint; warId: bigint; worldId: bigint; side: string; x: number; z: number; radiusM: number; turrets: number; medics: number; mechanics: number; updatedAt: { microsSinceUnixEpoch: bigint } }
interface TurretEventRow { battleId: bigint; worldId: bigint; kind: string; x: number; z: number; tx: number; tz: number; detail: string }

export const OUTPOST_RANGE_M = 900;
const SIDE_COLOR: Record<string, number> = { a: 0xe5534b, b: 0x4f9be8 };
const PROP_KEYS = ['prop:hinter_fence', 'prop:hinter_barrel', 'prop:hinter_brazier', 'prop:comms_relay_small', 'prop:market_stall'] as const;
type PropKey = (typeof PROP_KEYS)[number];
/** Natural heights the cooked props are scaled to (metres). */
const PROP_H: Record<PropKey, number> = { 'prop:hinter_fence': 1.5, 'prop:hinter_barrel': 1.0, 'prop:hinter_brazier': 1.3, 'prop:comms_relay_small': 3.2, 'prop:market_stall': 2.6 };
const FENCE_SEG_M = 4.8;

interface Turret { group: THREE.Group; head: THREE.Object3D; barrel: THREE.Object3D; flash: THREE.Sprite; flashT: number; aimYaw: number }
interface Built {
  key: string;
  row: OutpostRow;
  group: THREE.Group;
  turrets: Turret[];
  banner: THREE.Mesh | null;
  aidLamp: THREE.Sprite | null;
  bayLamp: THREE.Sprite | null;
  built: number;
  y: number;
  parts: number;
  /** THE OUTPOST DECAYS: 0 while its battle lives, then 0 → 1 over OUTPOST_DECAY_S from the row's resolution stamp. */
  decayK: number;
}

export class OutpostModule implements IModule {
  readonly name = 'OutpostModule';
  private readonly root = new THREE.Group();
  private readonly built = new Map<string, Built>();
  private readonly masters = new Map<PropKey, THREE.Object3D>();
  private readonly loading = new Set<PropKey>();
  private readonly bboxes = new Map<PropKey, THREE.Box3>();
  private subscribed = false;
  private disposed = false;
  private lastTurretFx: TurretEventRow[] = [];
  private turretsFired = 0;
  private readonly loadErrors: string[] = [];
  private readonly _v = new THREE.Vector3();

  constructor(private readonly env: EnvironmentModule, private readonly assets: AssetModule) {}

  init(app: App): void {
    this.root.name = 'outposts';
    app.world.scene.add(this.root);
    app.world.addSystem(makeSystem('OutpostRender', Phase.Animation, 42, (dt) => this.frame(dt)));
    // The generated outpost set (the Hugging Face outpost wave): bases built before it landed rebuild once.
    void prefetchOutpostProps(app).then(() => { for (const b of this.built.values()) b.built = 0; });
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    (window as unknown as Record<string, unknown>).__outposts = () => ({
      rows: this.rows().map((r) => ({ id: r.outpostId.toString(), battleId: r.battleId.toString(), side: r.side, x: Math.round(r.x), z: Math.round(r.z), r: r.radiusM, turrets: r.turrets, medics: r.medics, mechanics: r.mechanics, dist: Math.round(Math.hypot(r.x - atlas.localX, r.z - atlas.localZ)) })),
      built: [...this.built.values()].map((b) => ({ id: b.key, side: b.row.side, parts: b.parts, turrets: b.turrets.length, cooked: [...this.masters.keys()].length, y: Math.round(b.y * 10) / 10, decay: Math.round(b.decayK * 100) / 100 })),
      masters: [...this.masters.keys()],
      loading: [...this.loading],
      catalog: atlas.conn ? PROP_KEYS.map((k) => ({ key: k, assetId: prefabAssetId(atlas.conn!, k)?.toString() ?? null })) : [],
      errors: [...this.loadErrors],
      generated: outpostPropStats(),
      turretsFired: this.turretsFired,
    });
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subscribed) return;
    this.subscribed = true;
    try {
      conn.subscriptionBuilder().subscribe(['SELECT * FROM battle_outpost']);
      (conn.db as unknown as { battleEvent: { onInsert(cb: (c: unknown, row: TurretEventRow) => void): void } }).battleEvent.onInsert((_c, row) => {
        if (row.kind !== 'turret' || row.worldId !== atlas.currentWorldId) return;
        if (Math.hypot(row.x - atlas.localX, row.z - atlas.localZ) > OUTPOST_RANGE_M) return;
        this.lastTurretFx.push(row);
      });
    } catch { /* pre-publish server */ }
  }

  private rows(): OutpostRow[] {
    const conn = atlas.conn;
    if (!conn) return [];
    try { return [...(conn.db as unknown as { battleOutpost: { iter(): Iterable<OutpostRow> } }).battleOutpost.iter()]; } catch { return []; }
  }

  private ensureMasters(): void {
    const conn = atlas.conn;
    if (!conn) return;
    for (const key of PROP_KEYS) {
      if (this.masters.has(key) || this.loading.has(key)) continue;
      const id = prefabAssetId(conn, key);
      if (id === null) continue;
      this.loading.add(key);
      void this.assets.load(id).then((m) => {
        markShared(m);
        this.masters.set(key, m);
        const bb = new THREE.Box3().setFromObject(m);
        this.bboxes.set(key, bb);
        // A base built before this prop landed rebuilds once (self-heal).
        for (const b of this.built.values()) b.built = 0;
      }).catch((e) => { if (this.loadErrors.length < 8) this.loadErrors.push(`${key}: ${(e as Error)?.message ?? e}`); /* the part stays procedural */ }).finally(() => this.loading.delete(key));
    }
  }

  private frame(dt: number): void {
    if (this.disposed) return;
    const t = performance.now() / 1000;
    this.ensureMasters();
    const px = atlas.localX, pz = atlas.localZ;
    const seen = new Set<string>();
    for (const r of this.rows()) {
      if (r.worldId !== atlas.currentWorldId) continue;
      if (Math.hypot(r.x - px, r.z - pz) > OUTPOST_RANGE_M) continue;
      const key = r.outpostId.toString();
      seen.add(key);
      let b = this.built.get(key);
      if (b && b.built === 0) { this.drop(b); this.built.delete(key); b = undefined; }
      if (!b) { b = this.build(r); this.built.set(key, b); }
      b.row = r;
      this.tick(b, t, dt);
    }
    for (const [key, b] of this.built) if (!seen.has(key)) { this.drop(b); this.built.delete(key); }
    if (this.lastTurretFx.length) {
      const q = this.lastTurretFx; this.lastTurretFx = [];
      for (const e of q) this.fire(e);
    }
  }

  // ── Building ─────────────────────────────────────────────────────────────

  /** A cooked prop clone seated by its bbox bottom-centre at (x, y, z), scaled to a height (or a length along X). */
  private prop(key: PropKey, x: number, y: number, z: number, yaw: number, opts: { h?: number; len?: number } = {}): THREE.Object3D | null {
    const master = this.masters.get(key), bb = this.bboxes.get(key);
    if (!master || !bb) return null;
    const size = new THREE.Vector3(); bb.getSize(size);
    const ctr = new THREE.Vector3(); bb.getCenter(ctr);
    const sc = opts.len ? opts.len / Math.max(0.01, size.x) : (opts.h ?? PROP_H[key]) / Math.max(0.01, size.y);
    const clone = master.clone();
    clone.traverse((o) => { o.userData.sharedAsset = true; });
    const pivot = new THREE.Group();
    pivot.position.set(x, y, z);
    pivot.rotation.y = yaw;
    clone.scale.setScalar(sc);
    clone.position.set(-ctr.x * sc, -bb.min.y * sc, -ctr.z * sc);
    pivot.add(clone);
    return pivot;
  }

  /** A generated outpost prop seated at (x, y, z), or null (the previous piece stands in). */
  private gen(key: string, x: number, y: number, z: number, yaw: number, opts: { len?: number } = {}): THREE.Object3D | null {
    const g = outpostProp(key, yaw, opts);
    if (g) g.position.set(x, y, z);
    return g;
  }

  private box(w: number, h: number, d: number, color: number, x: number, y: number, z: number, yaw = 0): THREE.Mesh {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshStandardMaterial({ color, roughness: 0.75, metalness: 0.35 }));
    m.position.set(x, y + h / 2, z); m.rotation.y = yaw;
    m.castShadow = true; m.receiveShadow = true;
    return m;
  }

  private turret(x: number, y: number, z: number, color: number): Turret {
    const group = new THREE.Group();
    group.position.set(x, y, z);
    // The generated turret (one piece on its own round base) aims about its seat; the muzzle flash sits past its +Z face.
    const genTurret = outpostProp('outpost_turret_head');
    if (genTurret) {
      const box = new THREE.Box3().setFromObject(genTurret);
      const head = new THREE.Group();
      head.add(genTurret);
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 8), new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.4 }));
      eye.position.set(0, box.max.y * 0.9, box.max.z * 0.6);
      head.add(eye);
      const flash = spriteOf(glowTexture(), color, 1.4, true, 0);
      flash.position.set(0, box.max.y * 0.62, box.max.z + 0.35);
      head.add(flash);
      group.add(head);
      return { group, head, barrel: genTurret, flash, flashT: 0, aimYaw: 0 };
    }
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 1.1, 0.5, 12), new THREE.MeshStandardMaterial({ color: 0x4a4f57, roughness: 0.6, metalness: 0.6 }));
    base.position.y = 0.25; base.castShadow = true;
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.34, 1.4, 10), new THREE.MeshStandardMaterial({ color: 0x5c626b, roughness: 0.5, metalness: 0.7 }));
    post.position.y = 1.2;
    const head = new THREE.Group();
    head.position.y = 2.0;
    const housing = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.55, 1.1), new THREE.MeshStandardMaterial({ color: 0x3a3f47, roughness: 0.45, metalness: 0.75 }));
    housing.castShadow = true;
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 8), new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.4 }));
    eye.position.set(0, 0.32, 0.2);
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 1.4, 8), new THREE.MeshStandardMaterial({ color: 0x2a2d33, roughness: 0.4, metalness: 0.85 }));
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0.05, 1.1);
    head.add(housing, eye, barrel);
    const flash = spriteOf(glowTexture(), color, 1.4, true, 0);
    flash.position.set(0, 0.05, 1.9);
    head.add(flash);
    group.add(base, post, head);
    return { group, head, barrel, flash, flashT: 0, aimYaw: 0 };
  }

  private build(r: OutpostRow): Built {
    const group = new THREE.Group();
    group.name = `outpost:${r.outpostId}`;
    const color = SIDE_COLOR[r.side] ?? 0xcccccc;
    const y = this.env.groundAt(r.x, r.z);
    const R = Math.max(8, r.radiusM);
    let parts = 0;
    // The gate faces the FIELD: the battle target lies opposite the home town; the outpost stands OUTPOST_OFF_M toward home,
    // so the field is where the battle is — read it from the battle row when present, else +x.
    let fx = 1, fz = 0;
    try {
      const b = atlas.conn ? (atlas.conn.db as unknown as { battle: { battleId: { find(id: bigint): { targetX: number; targetZ: number } | undefined } } }).battle.battleId.find(r.battleId) : undefined;
      if (b) { const dx = b.targetX - r.x, dz = b.targetZ - r.z, d = Math.hypot(dx, dz) || 1; fx = dx / d; fz = dz / d; }
    } catch { /* pre-publish */ }
    const fieldYaw = Math.atan2(fx, fz);
    // Local frame: +Z toward the field.
    const local = (lx: number, lz: number): { x: number; z: number } => ({ x: r.x + lx * Math.cos(fieldYaw) + lz * Math.sin(fieldYaw), z: r.z - lx * Math.sin(fieldYaw) + lz * Math.cos(fieldYaw) });
    const add = (o: THREE.Object3D | null): void => { if (o) { group.add(o); parts++; } };
    const seat = (lx: number, lz: number): { x: number; y: number; z: number } => { const p = local(lx, lz); return { x: p.x, y: this.env.groundAt(p.x, p.z), z: p.z }; };
    // ── The walls: four sides of fence segments, the field side split by a GATE (a 6 m opening). ──
    const sides: Array<{ from: [number, number]; to: [number, number]; gate: boolean }> = [
      { from: [-R, -R], to: [R, -R], gate: false }, { from: [R, -R], to: [R, R], gate: false },
      { from: [-R, R], to: [-R, -R], gate: false }, { from: [-R, R], to: [R, R], gate: true },
    ];
    // The generated wall is an upright 1:2 PANEL (~0.9 m wide at its height): it tiles by its own width;
    // the older fence prop and the box span 4.8 m segments.
    const panelW = outpostPropSize('outpost_wall')?.[0] ?? 0;
    for (const s of sides) {
      const len = Math.hypot(s.to[0] - s.from[0], s.to[1] - s.from[1]);
      const seg = panelW > 0.2 ? panelW : FENCE_SEG_M;
      const n = Math.max(1, Math.round(len / seg)), L = len / n;
      const dirX = (s.to[0] - s.from[0]) / len, dirZ = (s.to[1] - s.from[1]) / len;
      for (let i = 0; i < n; i++) {
        const cx = s.from[0] + dirX * (i + 0.5) * L, cz = s.from[1] + dirZ * (i + 0.5) * L;
        if (s.gate && Math.abs(cx) < 3.2) continue; // the gate
        const p = seat(cx, cz);
        const yaw = fieldYaw - Math.atan2(dirZ, dirX) + Math.PI / 2; // the panel's local X runs along the span
        const wall = panelW > 0.2
          ? this.gen('outpost_wall', p.x, p.y, p.z, yaw)
          : this.prop('prop:hinter_fence', p.x, p.y, p.z, yaw, { len: L });
        add(wall ?? this.box(L, 1.4, 0.18, 0x6b6a62, p.x, p.y, p.z, yaw));
      }
    }
    // Gate posts, lit.
    for (const gx of [-3.6, 3.6]) { const p = seat(gx, R); add(this.gen('outpost_gate_post', p.x, p.y, p.z, fieldYaw) ?? this.box(0.5, 2.6, 0.5, 0x54585f, p.x, p.y, p.z)); }
    // ── The aid station (medics) on the left, the bay (mechanics) on the right, both facing inward. ──
    {
      const p = seat(-R * 0.55, -R * 0.35);
      // The generated hut's doorway is its +Z face (the contact sheet): turn it toward the base's centre.
      add(this.gen('outpost_aid_station', p.x, p.y, p.z, fieldYaw + Math.atan2(0.55, 0.35)) ?? this.prop('prop:market_stall', p.x, p.y, p.z, fieldYaw + Math.PI / 2) ?? this.box(4, 2.4, 3, 0x7d7a70, p.x, p.y, p.z, fieldYaw));
      const q = seat(-R * 0.55, -R * 0.35 + 3);
      add(this.prop('prop:hinter_barrel', q.x, q.y, q.z, 0) ?? this.box(0.8, 1, 0.8, 0x8a6f4a, q.x, q.y, q.z));
    }
    {
      const p = seat(R * 0.55, -R * 0.35);
      // The generated bay's roll-up door is its +Z face: turn it toward the base's centre.
      add(this.gen('outpost_repair_bay', p.x, p.y, p.z, fieldYaw + Math.atan2(-0.55, 0.35)) ?? this.prop('prop:market_stall', p.x, p.y, p.z, fieldYaw - Math.PI / 2) ?? this.box(4, 2.4, 3, 0x6f747d, p.x, p.y, p.z, fieldYaw));
      for (const [ox, oz] of [[R * 0.55 + 2.4, -R * 0.35 + 2.2], [R * 0.55 + 1.4, -R * 0.35 + 3.0], [R * 0.55 - 2.2, -R * 0.35 + 2.6]]) {
        const q = seat(ox, oz);
        add(this.prop('prop:hinter_barrel', q.x, q.y, q.z, (ox * 7) % 3) ?? this.box(0.8, 1, 0.8, 0x8a6f4a, q.x, q.y, q.z));
      }
    }
    // The comms relay at the back, the brazier by the gate.
    { const p = seat(R * 0.4, -R * 0.75); add(this.gen('outpost_comms_mast', p.x, p.y, p.z, fieldYaw) ?? this.prop('prop:comms_relay_small', p.x, p.y, p.z, fieldYaw) ?? this.box(0.6, 3.2, 0.6, 0x5a6c7a, p.x, p.y, p.z)); }
    // The bunkhouse at the back, facing the field (the generated set only — the old base had none).
    { const p = seat(-R * 0.2, -R * 0.72); add(this.gen('outpost_barracks', p.x, p.y, p.z, fieldYaw)); }
    {
      const p = seat(0, R * 0.45);
      const bz = this.prop('prop:hinter_brazier', p.x, p.y, p.z, 0);
      add(bz ?? this.box(0.9, 1.2, 0.9, 0x5c4a3a, p.x, p.y, p.z));
      // THE BRAZIER BURNS (fire realism R12, 2026-09-25): a fire-system anchor on the bowl (the 1.3 m prop, or the
      // 1.2 m box stand-in) — the flame, embers, smoke and a broker light, gone when the outpost's group goes.
      const top = bz ? PROP_H['prop:hinter_brazier'] * 0.8 : 1.2;
      const fire = fireAnchor({ kind: 'brazier', x: p.x, y: p.y + top, z: p.z, groundDrop: top });
      if (fire) group.add(fire);
    }
    // The banner pole: the side's colour over the base.
    let banner: THREE.Mesh | null = null;
    {
      const p = seat(0, -R * 0.2);
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 7, 8), new THREE.MeshStandardMaterial({ color: 0x8a8378, roughness: 0.7 }));
      pole.position.set(p.x, p.y + 3.5, p.z);
      group.add(pole); parts++;
      banner = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 1.3), new THREE.MeshStandardMaterial({ color, side: THREE.DoubleSide, roughness: 0.9, emissive: color, emissiveIntensity: 0.15 }));
      banner.position.set(p.x, p.y + 6.2, p.z);
      banner.rotation.y = fieldYaw;
      group.add(banner); parts++;
    }
    // Lamps over the aid station (green) and the bay (amber) — lit while the crews are there.
    const aidLamp = spriteOf(glowTexture(), 0x7ee08a, 1.6, true, 0.0);
    const bayLamp = spriteOf(glowTexture(), 0xf0b24a, 1.6, true, 0.0);
    { const p = seat(-R * 0.55, -R * 0.35); aidLamp.position.set(p.x, p.y + 3.2, p.z); }
    { const p = seat(R * 0.55, -R * 0.35); bayLamp.position.set(p.x, p.y + 3.2, p.z); }
    group.add(aidLamp, bayLamp);
    // ── The turrets: the two field-side corners. ──
    const turrets: Turret[] = [];
    for (let i = 0; i < Math.max(0, Math.min(4, r.turrets)); i++) {
      const inset = outpostPropSize('outpost_turret_head') ? 2.2 : 1.6; // the generated turret is ~2.6 m across
      const cx = i % 2 === 0 ? -R + inset : R - inset, cz = i < 2 ? R - inset : -R + inset;
      const p = seat(cx, cz);
      const tr = this.turret(p.x, p.y, p.z, color);
      tr.aimYaw = fieldYaw;
      tr.head.rotation.y = fieldYaw;
      group.add(tr.group); parts++;
      turrets.push(tr);
    }
    this.root.add(group);
    return { key: r.outpostId.toString(), row: r, group, turrets, banner, aidLamp, bayLamp, built: t0(), y, parts, decayK: 0 };
  }

  private tick(b: Built, t: number, dt: number): void {
    const r = b.row;
    // THE OUTPOST DECAYS (war tweaks 2026-09-17): once its battle is over the base runs down over OUTPOST_DECAY_S from the
    // row's resolution stamp — lamps out, the banner down, the turrets slump, the ground takes it, and over the last half
    // the pieces go one by one; the server reaps the row at the end (the sweep prunes it OUTPOST_DECAY_S after the stamp).
    const k = this.decayOf(r);
    b.decayK = k;
    const lit = 1 - k;
    if (b.aidLamp) (b.aidLamp.material as THREE.SpriteMaterial).opacity = (k > 0 ? 0.06 : r.medics > 0 ? 0.55 + Math.sin(t * 2.2) * 0.1 : 0.06) * lit;
    if (b.bayLamp) (b.bayLamp.material as THREE.SpriteMaterial).opacity = (k > 0 ? 0.06 : r.mechanics > 0 ? 0.5 + Math.sin(t * 1.7 + 1) * 0.1 : 0.06) * lit;
    if (b.banner) {
      b.banner.rotation.y += Math.sin(t * 1.3) * 0.002 * lit;
      b.banner.scale.y = Math.max(0.05, 1 - k * 1.4);
      (b.banner.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.15 * lit;
    }
    for (const tr of b.turrets) {
      // Track the last aim smoothly; flash decays.
      let d = tr.aimYaw - tr.head.rotation.y;
      if (d > 1e6 || d < -1e6) d = 0; // no heading is a million radians: a 3e38 f32 never shrinks under −2π and the loop below spun forever (bug sweep 2026-09-22)
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      tr.head.rotation.y += d * Math.min(1, dt * 5) * lit;
      tr.flashT = Math.max(0, tr.flashT - dt * 6);
      (tr.flash.material as THREE.SpriteMaterial).opacity = tr.flashT * lit;
      tr.group.rotation.z = -0.38 * k;           // the slump
      tr.group.scale.y = 1 - 0.3 * k;
    }
    b.group.position.y = -0.5 * k;               // the ground takes it (parts are placed in absolute coordinates)
    if (k > 0.5) {
      const gone = (k - 0.5) * 2;                  // the share of pieces gone over the last half
      const n = b.group.children.length;
      b.group.children.forEach((c, i) => { c.visible = ((i * 7919) % n) / n >= gone; });
    }
  }

  /** 0 while the outpost's battle lives; else the decay fraction from the row's resolution stamp (the server re-stamps at resolution). */
  private decayOf(r: OutpostRow): number {
    const conn = atlas.conn;
    if (!conn) return 0;
    let over = false;
    try {
      const bb = (conn.db as unknown as { battle: { battleId: { find(id: bigint): { phase: string } | undefined } } }).battle.battleId.find(r.battleId);
      over = !bb || bb.phase === 'resolved';
    } catch { over = false; }
    if (!over) return 0;
    const age = Date.now() / 1000 - Number(r.updatedAt.microsSinceUnixEpoch) / 1e6;
    return Math.max(0, Math.min(1, age / BATTLE.OUTPOST_DECAY_S));
  }

  /** A `turret` battle_event: the nearest turret of the outpost at (x, z) swings to the target and flashes. */
  private fire(e: TurretEventRow): void {
    this.turretsFired++;
    let best: Built | null = null, bd = 400;
    for (const b of this.built.values()) { const d = (b.row.x - e.x) ** 2 + (b.row.z - e.z) ** 2; if (d < bd) { bd = d; best = b; } }
    if (!best || best.turrets.length === 0) return;
    let tr = best.turrets[0], td = Infinity;
    for (const cand of best.turrets) { const d = (cand.group.position.x - e.tx) ** 2 + (cand.group.position.z - e.tz) ** 2; if (d < td) { td = d; tr = cand; } }
    tr.aimYaw = Math.atan2(e.tx - tr.group.position.x, e.tz - tr.group.position.z);
    tr.head.rotation.y = tr.aimYaw;
    tr.flashT = 1;
    this._v.set(e.tx, this.env.groundAt(e.tx, e.tz) + 1, e.tz);
  }

  private drop(b: Built): void {
    b.group.parent?.remove(b.group);
    b.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (o.userData.sharedAsset) return; // the cooked prop's buffers belong to the asset cache
      if (m.geometry) disposeLater(m.geometry);
      const mat = (m as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => disposeLater(x)); else if (mat && !(o as THREE.Sprite).isSprite) disposeLater(mat);
    });
  }

  dispose(): void {
    this.disposed = true;
    for (const b of this.built.values()) this.drop(b);
    this.built.clear();
    this.root.parent?.remove(this.root);
  }
}

function t0(): number { return performance.now(); }
